import debounce from 'lodash/debounce';
import React, { useCallback, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import LoadingIndicator from 'src/components/LoadingIndicator';
import { PreviewLocation } from 'src/components/Locations';
import MapHeaderAddress from 'src/components/MapHeaderAddress';
import NavTab from 'src/components/NavTab';
import PaginationBtns from 'src/components/PaginationBtns';
import {
  ShipmentQuotesList,
  ShipmentQuotesTable
} from 'src/components/ShipmentQuotes';
import { ShipmentsList, ShipmentsTable } from 'src/components/Shipments';
import emptyImg from 'src/images/empty.svg';
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormFeedback,
  FormGroup,
  Formik,
  FormInput,
  Icon,
  Input,
  Label,
  Nav,
  Row,
  ScreenSize,
  toast,
  useApp,
  useUrlState
} from 'straightline-ui';

export default function AddressDetail() {
  const app = useApp();
  const { id } = useParams();
  const service = app.service('api/address-details');
  const schema = service.schema;

  const {
    result: addressDetails,
    isLoading,
    isError,
    error
  } = service.useGet(id, { totals: true });

  const { update } = service.useUpdate();

  function onSubmit(data, formik) {
    update(id, data)
      .then(() => {
        formik.setSubmitting(false);
        toast.success('Address updated!');
        service.invalidateGet(id);
      })
      .catch((err) => {
        toast.errors(err);
        if (err.errors) {
          formik.setErrors(err.errors);
        }
        formik.setSubmitting(false);
      });
  }

  if (isLoading) {
    return (
      <div className="d-flex vh-100 align-items-center">
        <LoadingIndicator delay={350} />
      </div>
    );
  }

  if (isError) {
    return (
      <Container>
        <Row className="align-items-center justify-content-center vh-100">
          <Col lg="6" className="text-center">
            <img
              src={emptyImg}
              alt="Not Found"
              className="img-fluid animated fadeIn faster mb-5"
            />
            <h1>{error.message || 'There was an unexpected error'}</h1>
            <Button to="/dashboard" color="primary" size="lg" className="mr-3">
              <Icon type="home" className="mr-2" />
              Return to Dashboard
            </Button>
            <Button to="/support" color="secondary" size="lg">
              <Icon type="send" className="mr-2" />
              Contact Straightline
            </Button>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <React.Fragment>
      <Formik
        onSubmit={onSubmit}
        initialValues={addressDetails}
        validationSchema={schema}
        enableReinitialize
        render={(formik) => {
          return <PageContent formik={formik} />;
        }}
      />
    </React.Fragment>
  );
}

const addressQuery = (type, values) => {
  return {
    [`${type}.address_1`]: values.address.address_1,
    [`${type}.address_2`]: values.address.address_2,
    [`${type}.city`]: values.address.city,
    [`${type}.state`]: values.address.state,
    [`${type}.postal_code`]: values.address.postal_code,
    [`${type}.country`]: values.address.country
  };
};

function PageContent({ formik }) {
  const app = useApp();
  const { values } = formik;
  const [isEditing, setIsEditing] = useState(false);
  const { getInState } = useUrlState();

  const tab = getInState('tab', 'shipments');

  const pickupQuery = addressQuery('pickup_address', values);
  const deliveryQuery = addressQuery('delivery_address', values);

  const query = {
    $search: getInState('query.$search'),
    $skip: getInState('query.$skip'),
    $sort: getInState('query.$sort', { 'pickup_address.name': 1 }),
    $and: [
      {
        $or: [pickupQuery, deliveryQuery]
      }
    ]
  };

  const shipmentsQuery = app.service('api/shipments').useFind(
    {
      query
    },
    {
      enabled: tab === 'shipments',
      keepPreviousData: true
    }
  );

  const shipmentQuotesQuery = app.service('api/shipment-quotes').useFind(
    {
      query
    },
    {
      enabled: tab === 'shipmentQuotes',
      keepPreviousData: true
    }
  );

  const typeQuery = tab === 'shipments' ? shipmentsQuery : shipmentQuotesQuery;

  return (
    <React.Fragment>
      <MapHeaderAddress
        address={values.address}
        shipments={typeQuery.result?.data}
      />
      <Container className="pb-4" fluid>
        <AddressCard
          formik={formik}
          isEditing={isEditing}
          setIsEditing={setIsEditing}
        />
        {!isEditing && <ShipmentsCard formik={formik} typeQuery={typeQuery} />}
      </Container>
    </React.Fragment>
  );
}

function AddressCard({ formik, isEditing, setIsEditing }) {
  const app = useApp();
  const history = useHistory();
  const { values, setValues, isSubmitting } = formik;
  const { remove, isLoading: isRemoving } = app
    .service('api/address-details')
    .useRemove();

  const isDisabled = isSubmitting || isRemoving;

  function onRemove() {
    toast.confirm({
      title: 'Are you sure?',
      message:
        'Are you sure you want to remove this address? This cannot be undone.',
      okButtonText: 'Yes, remove address',
      cancelButtonText: "Nevermind, don't remove",
      handleOnOk: () => {
        return remove(values.id)
          .then(() => {
            toast.success('Address removed');
            history.push('/addresses');
          })
          .catch((err) => {
            toast.error(err.message);
          });
      }
    });
  }

  return (
    <Card className="mt-n6">
      <div className="px-4 py-4 d-flex justify-content-between align-items-top">
        <div
          style={{ maxWidth: 'calc(100% - 85px)' }}
          className="text-truncate"
        >
          <h2 className="card-header-title mb-1 text-truncate">
            {values.address?.name || 'Enter Name'}
            {values.default_address && (
              <span className="ml-2 small text-primary">
                Default{' '}
                {values.default_address.charAt(0).toUpperCase() +
                  values.default_address.slice(1)}
              </span>
            )}
          </h2>
          {values.address && (
            <div>
              <div className="text-truncate">{values.address.address_1}</div>
              <div className="text-truncate">
                {`${values.address.city}, ${values.address.state} ${values.address.postal_code}`}
              </div>
            </div>
          )}
          <div className="text-secondary text-truncate">
            {values.description || 'Description not available'}
          </div>
        </div>
        <div>
          <Button onClick={() => setIsEditing(!isEditing)}>
            {isEditing ? 'Close' : 'Edit'}
          </Button>
        </div>
      </div>
      {isEditing && (
        <CardBody className="px-3 px-md-4 border-top">
          <Form>
            <Row>
              <Col lg="6" className="mb-4 mb-lg-0">
                <FormGroup>
                  <Label>Address Name*</Label>
                  <FormInput
                    name="name"
                    disabled={isSubmitting}
                    placeholder="Enter your name for this address"
                  />
                  <FormFeedback name="name" />
                  <div className="text-muted mt-2">
                    This is a nickname for this address. It is used when
                    searching for saved address but is not shown on the shipment
                    or to carriers.
                  </div>
                </FormGroup>
                <FormGroup>
                  <Label>Address*</Label>
                  <FormInput
                    useAddress2
                    type="address"
                    name="address"
                    disabled={isDisabled}
                    invalid={false}
                    initialValue={values.address}
                    addressToOption={(address) => {
                      if (!address || !address.address_1) {
                        return undefined;
                      }
                      return {
                        label: `${address.address_1}, ${address.city}, ${address.state}, ${address.postal_code}`,
                        value: address
                      };
                    }}
                    onChange={({ target: { value } }) => {
                      if (!value) {
                        return setValues({
                          ...values,
                          address: null
                        });
                      }

                      setValues({
                        ...values,
                        address: {
                          ...values.address,
                          ...value,
                          name: values.address?.name || value.name || undefined
                        }
                      });
                    }}
                  />
                  <FormFeedback name="address" />
                </FormGroup>
                <FormGroup>
                  <Label>Company Name*</Label>
                  <FormInput
                    name="address.name"
                    disabled={isDisabled}
                    placeholder="Enter Company or Location name"
                  />
                  <FormFeedback name="address.name" />
                </FormGroup>
                <FormGroup>
                  <Label>Contact Name</Label>
                  <FormInput
                    name="address.contact"
                    disabled={isDisabled}
                    placeholder="Enter Contact Name"
                  />
                  <FormFeedback name="address.contact" />
                </FormGroup>
                <FormGroup>
                  <Label>Contact Phone</Label>
                  <FormInput
                    name="address.phone"
                    type="phone"
                    disabled={isDisabled}
                    placeholder="Enter Contact Phone"
                  />
                  <FormFeedback name="address.phone" />
                </FormGroup>
                <FormGroup>
                  <Label>Contact Email</Label>
                  <FormInput
                    name="address.email"
                    disabled={isDisabled}
                    placeholder="Enter Contact Email"
                  />
                  <FormFeedback name="address.email" />
                </FormGroup>
                <FormGroup>
                  <Label>Description</Label>
                  <FormInput
                    name="description"
                    type="textarea"
                    rows="4"
                    disabled={isDisabled}
                    placeholder="Enter details or notes about the address"
                  />
                  <FormFeedback name="description" />
                </FormGroup>
                <FormGroup>
                  <Label>Default Appointment</Label>
                  <FormInput
                    name="address.appointment"
                    rows="4"
                    disabled={isDisabled}
                    placeholder="Enter a default appointment"
                  />
                  <FormFeedback name="address.appointment" />
                </FormGroup>
                <FormGroup>
                  <Label>Default Address</Label>
                  <FormInput
                    name="default_address"
                    type="radio-buttons"
                    disabled={isDisabled}
                    emptyValue={null}
                    options={[
                      { label: 'Pickup', value: 'pickup' },
                      { label: 'Delivery', value: 'delivery' }
                    ]}
                    onChange={({ target: { value } }) => {
                      setValues({
                        ...values,
                        default_address:
                          value === values.default_address ? null : value
                      });
                    }}
                  />
                  <div className="text-secondary mt-2">
                    Default address will automatically be applied to new quotes.
                  </div>
                </FormGroup>
                <div className="d-flex justify-content-between">
                  <Button
                    type="button"
                    color="danger"
                    disabled={isDisabled}
                    className="px-5"
                    onClick={onRemove}
                  >
                    Remove
                  </Button>
                  <Button
                    type="submit"
                    color="primary"
                    disabled={isDisabled}
                    className={isSubmitting ? 'is-loading px-5' : 'px-5'}
                  >
                    Submit
                  </Button>
                </div>
              </Col>
              <Col lg="6">
                <PreviewLocation values={values} />
              </Col>
            </Row>
          </Form>
        </CardBody>
      )}
    </Card>
  );
}

function ShipmentsCard({ formik, typeQuery }) {
  const { values } = formik;
  const { getInState, setInState, delInState } = useUrlState();
  const { result, isLoading, isError, error } = typeQuery;
  const tab = getInState('tab', 'shipments');
  const $search = getInState('query.$search', '');
  const [text, setText] = useState($search);

  // eslint-disable-next-line
  const debouncedHandleChange = useCallback(
    debounce((value) => {
      const val = value && value.trim ? value.trim() : value;
      if (val) {
        setInState('query.$search', val);
      } else {
        delInState('query.$search');
      }
    }, 300),
    []
  );

  const handleSearchChange = ({ target: { value } }) => {
    setText(value);
    debouncedHandleChange(value);
  };

  const handleSearchClear = () => {
    setText('');
    delInState('query.$search');
  };

  // if (isLoading || !result?.data) {
  //   return null;
  // }

  if (isError) {
    return (
      <Card>
        <CardBody>
          <img
            src={emptyImg}
            alt="Not Found"
            className="img-fluid animated fadeIn faster mb-5"
          />
          <h1>{error.message || 'There was an unexpected error'}</h1>
          <Button to="/dashboard" color="primary" size="lg" className="mr-3">
            <Icon type="home" className="mr-2" />
            Return to Dashboard
          </Button>
          <Button to="/support" color="secondary" size="lg">
            <Icon type="send" className="mr-2" />
            Contact Straightline
          </Button>
        </CardBody>
      </Card>
    );
  }

  return (
    <React.Fragment>
      <Card>
        <div className="card-header">
          <Row className="justify-content-between">
            <Col md="auto">
              <Nav tabs className="card-header-tabs">
                <NavTab
                  tab="shipments"
                  onClick={() => setInState('tab', 'shipments')}
                  activeTab={tab}
                >
                  Shipments
                  <Badge color="light" pill className="ml-2">
                    {values.totals.shipments.pickups +
                      values.totals.shipments.deliveries}
                  </Badge>
                </NavTab>
                <NavTab
                  tab="shipmentQuotes"
                  onClick={() => setInState('tab', 'shipmentQuotes')}
                  activeTab={tab}
                >
                  Quotes
                  <Badge color="light" pill className="ml-2">
                    {values.totals.shipmentQuotes.pickups +
                      values.totals.shipmentQuotes.deliveries}
                  </Badge>
                </NavTab>
              </Nav>
            </Col>
            <Col
              className="border-left d-none d-md-block"
              style={{
                // Allow the left border to extend past
                // card-header padding to reach top/bottom borders
                paddingTop: '0.5rem',
                paddingBottom: '0.5rem',
                marginTop: '-0.5rem',
                marginBottom: '-0.5rem'
              }}
              md="4"
            >
              <Row className="align-items-center">
                <Col>
                  <Row className="align-items-center">
                    <Col className="col-auto pr-0">
                      <Icon type="search" className="text-muted" />
                    </Col>
                    <Col>
                      <Input
                        value={text}
                        onChange={handleSearchChange}
                        className="form-control-flush"
                        placeholder="Search"
                      />
                    </Col>
                    <Col className="col-auto p-0">
                      <Button
                        // disabled={!text}
                        color="default"
                        type="button"
                        onClick={handleSearchClear}
                      >
                        <Icon type="x" />
                      </Button>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Col>
          </Row>
        </div>
        <ScreenSize lg xl>
          {() =>
            tab === 'shipments' ? (
              <ShipmentsTable data={result?.data} isLoading={isLoading} />
            ) : (
              <ShipmentQuotesTable data={result?.data} isLoading={isLoading} />
            )
          }
        </ScreenSize>
        <ScreenSize xs sm md>
          {() =>
            tab === 'shipments' ? (
              <ShipmentsList data={result?.data} isLoading={isLoading} />
            ) : (
              <ShipmentQuotesList data={result?.data} isLoading={isLoading} />
            )
          }
        </ScreenSize>
      </Card>
      <div className="text-right">
        <PaginationBtns
          total={result?.total}
          skip={getInState('query.$skip', 0)}
          setSkip={($skip) => setInState('query.$skip', $skip)}
        />
      </div>
    </React.Fragment>
  );
}
